import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Play, Pause } from "lucide-react";
import { Link } from "react-router-dom";
import Button from "../ui/Button";

gsap.registerPlugin(ScrollTrigger);

const CommonListSection = ({
  subtitle,
  title,
  description,
  items = [],
  media = [],
  imageAlt = "Section Media",
  autoPlayInterval = 8000,
}) => {
  const sectionRef = useRef(null);
  const mediaContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false); // Initially false for video
  const [isStarted, setIsStarted] = useState(false); // Track if video has been clicked/started

  const getYouTubeId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const currentMediaUrl = media[currentIndex];
  const youtubeId = getYouTubeId(currentMediaUrl);

  // Play/Pause and Start Logic
  const handlePlayClick = () => {
    if (!isStarted) {
      setIsStarted(true);
      setIsPlaying(true);
    } else {
      togglePlay();
    }
  };

  const togglePlay = () => {
    if (!iframeRef.current) return;
    const command = isPlaying
      ? '{"event":"command","func":"pauseVideo","args":""}'
      : '{"event":"command","func":"playVideo","args":""}';

    iframeRef.current.contentWindow.postMessage(command, "*");
    setIsPlaying(!isPlaying);
  };

  // Auto-slide effect (Only if video is not started or we are showing images)
  useEffect(() => {
    if (media.length <= 1 || isStarted) return;

    const timer = setInterval(() => {
      gsap.to(mediaContainerRef.current, {
        opacity: 0,
        duration: 0.5,
        onComplete: () => {
          setCurrentIndex((prev) => (prev + 1) % media.length);
          gsap.to(mediaContainerRef.current, { opacity: 1, duration: 0.5 });
        },
      });
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [media, autoPlayInterval, isStarted]);

  return (
    <div
      ref={sectionRef}
      className="flex justify-between items-center w-full max-w-[1612px] m-auto px-[32px] py-10"
    >
      {/* Left Content */}
      <div className="flex flex-col gap-[25px] sm:gap-[50px] justify-start items-center w-full lg:w-[54%]">
        <div className="flex flex-col gap-[12px] w-full">
          <div className="about-subtitle flex justify-start items-center w-full">
            <p className="animated-text-content text-[12px] sm:text-[24px] font-[Sora]">
              {subtitle}
            </p>
            <div className="about-divider-line w-[100px] sm:w-[200px] h-[2px] bg-black ml-[15px]"></div>
          </div>
          <h2 className="animated-text-content text-[30px] sm:text-[60px] font-bold font-[Sora] w-full leading-tight">
            {title}
          </h2>
          <p className="animated-text-content text-[12px] sm:text-[24px] text-justify font-[Sora] w-full">
            {description}
          </p>
        </div>
        <div className="flex flex-col gap-[24px] w-full">
          {items.map((item, index) => (
            <div key={index} className="flex gap-[10px] items-start w-full">
              <div className="flex items-center justify-center w-[30px] sm:w-[50px] h-[30px] sm:h-[50px] bg-[#1C233A] rounded-xl flex-shrink-0">
                {item.icon && <img src={item.icon} alt="" className="w-1/2" />}
              </div>
              <div className="flex flex-col flex-1">
                <h3 className="animated-text-content text-[18px] sm:text-[30px] font-bold font-[Sora]">
                  {item.title}
                </h3>
                <p className="animated-text-content text-[14px] sm:text-[24px] text-justify font-[Sora]">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="overflow-hidden w-full flex justify-start items-start mlt-0 sm:ml-[120px]">
          {/* <Link
            to="https://utpann.in/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              text="Learn More"
              text_font_size="16"
              text_font_family="Sora"
              text_font_weight="700"
              text_line_height="20px"
              text_text_align="center"
              text_color="#ffffff"
              border_border="1px solid #ffffff"
              border_border_radius="30px"
              padding="6px 17px"
              className="mt-[20px] w-auto text-[16px] sm:text-[20px] 2xl:text-[24px] leading-[20px] sm:leading-[25px] 2xl:leading-[31px] py-[6px] px-[17px] sm:py-[9px] sm:px-[25px] 2xl:py-[12px] 2xl:px-[34px] "
            />
          </Link> */}
          <Link
            to="https://utpann.in/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              text="Learn More"
              // Default Styling (can be overridden via buttonProps)
              text_font_size="16"
              text_font_family="Sora"
              text_font_weight="700"
              text_line_height="20px"
              text_text_align="center"
              text_color="white"
              fill_background_color="#1C233A"
              border_border_radius="30px"
              border_border="none"
              padding="6px 17px"
              className="w-auto text-[16px] sm:text-[2   0px] 2xl:text-[24px] leading-[20px] sm:leading-[25px] 2xl:leading-[31px] py-[6px] px-[17px] sm:py-[9px] sm:px-[25px] 2xl:py-[12px] 2xl:px-[34px]"
            />
          </Link>
        </div>
      </div>

      {/* Right Media Section */}
      <div className="w-full lg:w-[40%] bg-gray-100 hidden lg:block overflow-hidden relative group aspect-video h-[100vh] rounded-2xl">
        <div ref={mediaContainerRef} className="w-full h-full relative">
          {youtubeId ? (
            <div className="relative w-full h-full bg-black">
              {/* SHOWN ONLY BEFORE START: Thumbnail Image */}
              {!isStarted && (
                <img
                  src={`/images/img_image123.png`}
                  alt="Thumbnail"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}

              {/* SHOWN ONLY AFTER CLICK: YouTube Iframe */}
              {isStarted && (
                <iframe
                  ref={iframeRef}
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&autoplay=1&mute=0&rel=0`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                ></iframe>
              )}

              {/* OVERLAY BUTTON: Controls play/start */}
              <button
                onClick={handlePlayClick}
                className={`absolute inset-0 w-full h-full flex items-center justify-center transition-all duration-500 z-10 
                  ${
                    isPlaying && isStarted
                      ? "opacity-0 bg-transparent group-hover:opacity-100 group-hover:bg-black/20"
                      : "opacity-100 bg-black/40"
                  }`}
              >
                <div className="w-[80px] h-[80px] bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center text-black shadow-2xl transform transition-transform hover:scale-110">
                  {isPlaying && isStarted ? (
                    <Pause size={35} fill="black" />
                  ) : (
                    <Play size={35} fill="black" className="ml-1" />
                  )}
                </div>
              </button>
            </div>
          ) : (
            /* Regular Image if no YouTube link */
            <img
              src={currentMediaUrl}
              alt={imageAlt}
              className="object-cover w-full h-full"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CommonListSection;
